import { Buffer } from 'buffer';

(globalThis as typeof globalThis & { Buffer: typeof Buffer }).Buffer = Buffer;
if (typeof window !== 'undefined') {
  (window as unknown as Window & { Buffer: typeof Buffer }).Buffer = Buffer;
}

export type EnvName = 'draft' | 'preview' | 'production';

export type EnvConfig = {
  tarobaseAppId?: string;
  appId?: string;
  backendUrl?: string;
  apiUrl?: string;
  authApiUrl?: string;
  wsApiUrl?: string;
  wsUrl?: string;
  url?: string;
  hosts?: string[];
};

export type PoofConfig = {
  projectId?: string;
  slug?: string;
  generationMode?: string;
  environments?: Partial<Record<EnvName, EnvConfig>>;
  apiUrl?: string;
  authApiUrl?: string;
  wsApiUrl?: string;
  wsUrl?: string;
  draft?: EnvConfig;
  preview?: EnvConfig;
  production?: EnvConfig;
};

export type ResolvedEnv = {
  name: EnvName;
  rule: 'override' | 'mock-auth' | 'localhost' | 'hostname' | 'default';
  config: EnvConfig;
};

export type InitStatus = 'idle' | 'loading' | 'ready' | 'error';
type Listener = (status: InitStatus, error?: Error) => void;

const DEFAULT_MOCK_ADDRESS = 'HKbZbRR7jWWR5VRN8KFjvTCHEzJQgameYxKQxh2gPoof';

let initPromise: Promise<void> | null = null;
let status: InitStatus = 'idle';
let error: Error | null = null;
let loadedConfig: PoofConfig | null = null;
let resolvedEnv: ResolvedEnv | null = null;
let mockAuthEnabled = false;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) listener(status, error ?? undefined);
}

export function subscribeInit(listener: Listener): () => void {
  listeners.add(listener);
  listener(status, error ?? undefined);
  return () => {
    listeners.delete(listener);
  };
}

export function getInitStatus(): InitStatus {
  return status;
}

export function getLoadedConfig(): PoofConfig | null {
  return loadedConfig;
}

export function getResolvedEnv(): ResolvedEnv | null {
  return resolvedEnv;
}

export function isMockAuth(): boolean {
  return mockAuthEnabled;
}

function query(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URL(window.location.href).searchParams;
}

function detectMockAuth(): boolean {
  const value = query().get('mockAuth');
  return value === 'true' || value === '1';
}

function queryAppId(): string | undefined {
  const appId = query().get('appId');
  return appId?.trim() || undefined;
}

function mockAddress(): string {
  return query().get('mockAddress')?.trim() || DEFAULT_MOCK_ADDRESS;
}

function normalizeEnvName(value: string | null | undefined): EnvName | null {
  if (!value) return null;
  const v = value.toLowerCase();
  if (v === 'draft' || v === 'preview' || v === 'production') return v;
  if (v === 'prod') return 'production';
  return null;
}

function getEnvConfig(config: PoofConfig, name: EnvName): EnvConfig | undefined {
  return config.environments?.[name] || config[name];
}

function isLocalhost(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname.endsWith('.localhost')
  );
}

function matchHost(host: string, entry: string): boolean {
  const candidate = entry.trim().toLowerCase();
  if (!candidate) return false;
  if (candidate.startsWith('*.')) {
    const suffix = candidate.slice(1);
    return host.endsWith(suffix);
  }
  return host === candidate;
}

function resolveEnv(config: PoofConfig): ResolvedEnv {
  const override = normalizeEnvName(query().get('env'));
  if (override) {
    const envConfig = getEnvConfig(config, override);
    if (envConfig) return { name: override, rule: 'override', config: envConfig };
  }

  if (detectMockAuth()) {
    const envConfig = getEnvConfig(config, 'draft');
    if (envConfig) return { name: 'draft', rule: 'mock-auth', config: envConfig };
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  const host = typeof window !== 'undefined' ? window.location.host.toLowerCase() : '';

  if (hostname && isLocalhost(hostname)) {
    const envConfig = getEnvConfig(config, 'draft');
    if (envConfig) return { name: 'draft', rule: 'localhost', config: envConfig };
  }

  if (host) {
    for (const name of ['draft', 'preview', 'production'] as EnvName[]) {
      const envConfig = getEnvConfig(config, name);
      const hosts = envConfig?.hosts ?? [];
      if (hosts.some((entry) => matchHost(host, entry))) {
        return { name, rule: 'hostname', config: envConfig! };
      }
    }
  }

  const fallback =
    getEnvConfig(config, 'production') || getEnvConfig(config, 'preview') || getEnvConfig(config, 'draft');
  if (!fallback) {
    throw new Error('poof.config.json has no environments configured');
  }
  const name: EnvName = getEnvConfig(config, 'production')
    ? 'production'
    : getEnvConfig(config, 'preview')
      ? 'preview'
      : 'draft';
  return { name, rule: 'default', config: fallback };
}

async function loadConfig(): Promise<PoofConfig> {
  const response = await fetch('/poof.config.json', { cache: 'no-store' });
  if (!response.ok) throw new Error(`poof.config.json fetch failed: ${response.status}`);
  return (await response.json()) as PoofConfig;
}

function pickAppId(envConfig: EnvConfig): string | undefined {
  return queryAppId() || envConfig.tarobaseAppId || envConfig.appId;
}

function pickEndpoints(
  config: PoofConfig,
  envConfig: EnvConfig,
): {
  apiUrl: string;
  authApiUrl: string;
  wsApiUrl: string;
} {
  return {
    apiUrl: envConfig.apiUrl || config.apiUrl || 'https://api.tarobase.com',
    authApiUrl: envConfig.authApiUrl || config.authApiUrl || 'https://auth.tarobase.com',
    wsApiUrl:
      envConfig.wsApiUrl ||
      envConfig.wsUrl ||
      config.wsApiUrl ||
      config.wsUrl ||
      'wss://api.tarobase.com/ws/v2',
  };
}

export function startInit(): Promise<void> {
  if (initPromise) return initPromise;
  status = 'loading';
  error = null;
  notify();

  initPromise = (async () => {
    try {
      const config = await loadConfig();
      loadedConfig = config;

      const env = resolveEnv(config);
      resolvedEnv = env;
      if (typeof window !== 'undefined') {
        (window as unknown as { __poofEnv?: ResolvedEnv }).__poofEnv = env;
      }
      console.info(`[poof] env=${env.name} rule=${env.rule}`);

      mockAuthEnabled = detectMockAuth() && env.name !== 'production';
      const address = mockAddress();
      if (mockAuthEnabled) {
        sessionStorage.setItem('test-user-address', address);
        sessionStorage.setItem('mock-auth-explicit-login', 'true');
        if (!localStorage.getItem('tarobase_last_auth_method')) {
          localStorage.setItem('tarobase_last_auth_method', 'phantom');
        }
      }

      const appId = pickAppId(env.config);
      if (!appId) {
        throw new Error(
          `Missing tarobaseAppId for env="${env.name}" in poof.config.json (rule=${env.rule}). Populate environments.${env.name}.tarobaseAppId or pass ?appId=.`,
        );
      }

      const endpoints = pickEndpoints(config, env.config);
      const chain = env.name === 'draft' ? 'offchain' : 'solana_mainnet';
      const rpcUrl = env.name === 'draft' ? `${endpoints.apiUrl}/app/${appId}/rpc` : undefined;

      const sdk = await import('@pooflabs/web');
      const constantsModule = await import('./constants').catch(() => ({} as Record<string, unknown>));
      const privyCustomAppId = (constantsModule as Record<string, unknown>).PRIVY_CUSTOM_APP_ID as
        | string
        | undefined;
      const phantomAppId = (constantsModule as Record<string, unknown>).PHANTOM_APP_ID as string | undefined;

      const sdkConfig: Record<string, unknown> = {
        apiKey: '',
        appId,
        apiUrl: endpoints.apiUrl,
        authApiUrl: endpoints.authApiUrl,
        wsApiUrl: endpoints.wsApiUrl,
        authMethod: 'phantom',
        chain,
        skipBackendInit: true,
        ...(rpcUrl ? { rpcUrl } : {}),
        ...(mockAuthEnabled ? { mockAuth: true, mockAddress: address } : {}),
      };

      if (phantomAppId) {
        sdkConfig.phantomConfig = {
          appId: phantomAppId,
          enablePrivyFallback: false,
        };
      } else {
        sdkConfig.phantomConfig = {
          appId: 'c6db8bcd-379f-47a5-829f-2aed09d51d87',
        };
      }

      if (privyCustomAppId) {
        sdkConfig.privyConfig = {
          appId: privyCustomAppId,
          config: {
            appearance: {
              walletChainType: 'solana-only',
            },
          },
        };
      }

      await sdk.init(sdkConfig);

      status = 'ready';
      error = null;
      notify();
    } catch (err) {
      status = 'error';
      error = err instanceof Error ? err : new Error(String(err));
      notify();
      throw error;
    }
  })();

  return initPromise;
}

export async function ensureSignedIn(): Promise<{ address: string }> {
  await startInit();
  const sdk = await import('@pooflabs/web');
  const existing = sdk.getCurrentUser?.();
  if (existing?.address) return { address: existing.address };
  const user = await sdk.login();
  if (!user?.address) throw new Error('Poof login did not return a wallet address');
  return { address: user.address };
}
